import { EmptyState, LoadingState, View } from "components/Shared";
import _ from "lodash";
import React, { memo } from "react";
import { translate } from "lib";
import Collection from "./Collection";

/**
 * The property workspace's "Leases" tab — every LEASE AGREEMENT on this property
 * (GET /v2/lease-agreements?property_id=:id).
 *
 * ---------------------------------------------------------------------------
 * WHY THIS REPLACED components/Leases/PropertyCollection
 * ---------------------------------------------------------------------------
 * The old tab rendered `property.leases`, i.e. TCP's own `Lease` rows reached
 * through `Property has_many :leases, through: :tenancies`. Since the amendment
 * ERD `/lease_data` writes a `LeaseAgreement` instead and never touches
 * `leases`, so on any Yardi-fed property that tab said "No Leases found" while
 * the property in fact had leases. This one asks for the record Yardi writes.
 *
 * Same Collection as the top-level list, so the control bar, rows and pager
 * behave identically — and the same shape as the Tenancies and Tenants tabs
 * beside it.
 *
 * SCOPING IS THE PROPERTY, and so is the top-level list since 31 Aug 2026: the
 * API authorizes the PROPERTY here (PropertyPolicy#lease_agreements? — a
 * property administrator, a member of property.users) and then returns every
 * agreement on it, the way V2::TenantsController and V2::TenanciesController
 * already answer for this workspace. A property's lease list that quietly
 * omitted rows would be a worse lie than showing them, and the audience is now
 * exactly the group entitled to the whole book. The top-level list is the same
 * rule spread across every property the caller administers.
 *
 * NON-ADMINISTRATORS GET THE TAB AND A REASON, NOT A MISSING TAB (Sim,
 * 31 Aug 2026). `isAdministrator` comes from Properties/Show.tsx, which computes
 * it the same way the rest of that workspace does — membership of
 * `property.users`, the very set PropertyPolicy#lease_agreements? checks — so the
 * client and the API cannot disagree about who is refused. Rendering the message
 * INSTEAD of <Collection/> also means no request is made: the 403 that would
 * otherwise land in the console is not interesting, the sentence is.
 */

interface Props {
	property: tcpinpoint.Property;
	isAdministrator?: boolean;
	name?: string;
	icon?: string;
}

const PropertyTab = ({ property, isAdministrator }: Props) => {
	const propertyId = _.get(property, "id");
	// Held back until the id exists: Collection reads `propertyId` being unset as
	// "this is the global list" and would fetch every lease in the estate.
	if (!propertyId) return <LoadingState />;

	// The shared empty-state box, as the Contacts and Assets tabs use it, but with
	// its own sentence rather than `resource="lease_agreements"` — this is not an
	// empty list, it is a list withheld.
	if (!isAdministrator)
		return (
			<View restricted title="lease_agreements">
				<EmptyState>
					<div>{translate("leases_restricted_to_property_admin")}</div>
				</EmptyState>
			</View>
		);

	return (
		<View restricted title="lease_agreements">
			<Collection propertyId={propertyId} />
		</View>
	);
};

export default memo(PropertyTab);
